import { Component, OnDestroy, OnInit } from '@angular/core';
import { Company, CompanyForKpiDTO } from '../../../pp/dto/Company';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/service/auth.service';
import { firstValueFrom, Subject } from 'rxjs';
import { CompanyBackendService } from '../../../pp/service/company-backend.service';
import {
  OrganizationAccesses,
  OrganizationsService,
} from '../../../organization/components/service/organizations.service';
import { ActivatedRoute, Router } from '@angular/router';
import { getKpiCompanyMetricsUrl } from '../../module/kpi-module-routing.module';
import { Organization } from '../../../organization/dto/Organization';
import { Employee } from '../../../organization/dto/Employee';
import { fadeInOutAfterFirstAnimation, fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { ToolTipSettings } from 'src/app/domain/shared/component/tooltip/tooltip.util';
import { GroupingKpiBulkRequest, ShowGroupingWrapper } from "../../service/internal/track-untrack-grouping-helper";
import { PortfolioAnalysisService } from "../../service/backend/portfolio-analysis.service";
import { throwErrorOnEmpty } from "../../../../util/error/pipe-errors";
import { KpiRecipient } from "../../dto/KpiMember";
import { KpiRequestCompanyMini } from "../../modals/kpi-modal-bulk-kpi-request/kpi-modal-bulk-kpi-request.component";
import {
  CrudKpiMemberResponse,
} from "../../modals/kpi-modal-company-member-crud/kpi-modal-company-member-crud.component";
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../../../core/service/delay.service";

@Component({
  selector: 'app-kpi-companies',
  templateUrl: './kpi-companies.component.html',
  styleUrls: ['./kpi-companies.component.scss'],
  animations: [fadeInOutAnimation(), fadeInOutAfterFirstAnimation()],
  providers: [DelayService],
})
export class KpiCompaniesComponent implements OnInit, OnDestroy {
  private _destroyStream = new Subject<void>();
  protected readonly window = window;
  companies: CompanyForKpiDTO[];
  selected = new KpiCompaniesSelected();

  isAdmin = false;
  show = new KpiCompaniesShow();
  orgAccesses: OrganizationAccesses;
  tooltipSettings: ToolTipSettings = { show: false };

  MAX_DESC_LENGTH = 120;
  readonly grouping: GroupingKpiBulkRequest[] = ['tracked', 'untracked'];

  constructor(private authService: AuthService,
    private companyService: CompanyBackendService,
    private orgService: OrganizationsService,
    private router: Router,
    private route: ActivatedRoute,
    private portfolioAnalysisService: PortfolioAnalysisService,
    private delayService: DelayService,
  ) {
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.delayService.initSubscribes(
      DEFAULT_DROP_DOWN_DELAY_TIME,
      () => this.showTooltip(),
      () => this.hideTooltip(),
    );
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async closeCompanyCrudModal(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.getCompanies();
    }
    this.selected.company = null;
    this.show.companyCrud = false;
  }

  goToCompany(company: Company): void {
    const url = getKpiCompanyMetricsUrl(company.uuid);
    this.router.navigate([url], { queryParams: this.route.snapshot.queryParams });
  }

  fillTooltipData(id: string, value: string, maxLen: number): void {
    if (!value || value === '\n' || value.length < maxLen) {
      return;
    }
    this.tooltipSettings = {
      show: false,
      coordinates: document.getElementById(id).getBoundingClientRect(),
      title: value,
    };
    this.delayService.activate();
  }

  deactivateTooltip(): void {
    this.delayService.deactivate();
  }

  showTooltip(): void {
    this.tooltipSettings.show = true;
  }

  hideTooltip(): void {
    this.tooltipSettings.show = false;
  }

  async closeEmployeeModal(response: CrudKpiMemberResponse): Promise<void> {
    if (response) {
      await this.getCompanies();
    }
    this.show.teamCrud = false;
  }

  async closeCompanyBulkUpload(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.getCompanies();
    }
    this.show.companyBulkUpload = false;
  }

  async closeCompanyBulkDelete(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.getCompanies();
    }
    this.show.companyBulkDelete = false;
  }

  async updateTrackStatus(company: CompanyForKpiDTO, isTracked: boolean): Promise<void> {
    await firstValueFrom(this.portfolioAnalysisService.updateIsTrackedStatus(company.company.uuid, isTracked)
      .pipe(
        takeUntil(this._destroyStream),
        throwErrorOnEmpty(undefined, 'Could not update track status in kpi metrics')
      ));

    company.company.isKpiPortfolioTracked = isTracked;
    this.updateGroupingAmount();
  }

  addCompanyMember(wrapper: CompanyForKpiDTO): void {
    this.selected.memberModalParams = {
      member: null,
      isDeleteEnabled: false,
      company: {
        uuid: wrapper.company.uuid,
        name: wrapper.company.name,
      },
    };
    this.show.teamCrud = true;
  }

  editCompanyMember(wrapper: CompanyForKpiDTO, member: Employee): void {
    this.selected.memberModalParams = {
      member: {
        companyUUID: wrapper.company.uuid,
        uuid: member.uuid,
        name: member.name,
        family: member.family,
        companyName: member.companyName,
        position: member.position,
        mail: member.mail,
        phone: member.phone,
      },
      isDeleteEnabled: false,
      company: {
        uuid: wrapper.company.uuid,
        name: wrapper.company.name,
      },
    };
    this.show.teamCrud = true;
  }

  async closeDeleteCompanyModal(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await firstValueFrom(this.companyService.bulkCompaniesDelete([this.selected.company.uuid]));
      await this.getCompanies();
    }
    this.show.companyDeleteThinkTwice = false;
  }

  private initSubscriptions(): void {
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        if (!userType) {
          return;
        }
        this.isAdmin = userType === 'admin';
      });

    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => {
          this.selected.org = org;
          this.orgAccesses = this.orgService.selectedOrgAccesses;
        }),
        switchMap(() => this.getCompanies()),
        takeUntil(this._destroyStream))
      .subscribe();
  }

  private async getCompanies(): Promise<void> {
    this.companies = await firstValueFrom(
      this.companyService.getByOrgWithEmployees(this.selected.org.uuid)
        .pipe(takeUntil(this._destroyStream)));
    this.updateGroupingAmount();
  }

  private updateGroupingAmount(): void {
    this.show.grouping.tracked.amount = this.companies.filter((company) => company.company.isKpiPortfolioTracked).length;
    this.show.grouping.untracked.amount = this.companies.filter((company) => !company.company.isKpiPortfolioTracked).length;
  }
}

class KpiCompaniesShow {
  companyCrud = false;
  companyBulkUpload = false;
  companyBulkDelete = false;
  companyDeleteThinkTwice = false;
  teamCrud = false;
  grouping = new ShowGroupingWrapper();
}

class KpiCompaniesSelected {
  org: Organization;
  company: Company;
  memberModalParams: {
    member: KpiRecipient;
    isDeleteEnabled: boolean;
    company: KpiRequestCompanyMini;
  };
}
